import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export type StatsPeriod = 'today' | 'yesterday' | 'week' | 'month' | 'custom'

export interface KpiStat {
  current: number | null
  previous: number | null
  change_pct: number | null
  sparkline?: number[]
}

export interface TimeSeries {
  labels: string[]
  series: { name: string; data: number[] }[]
}

export interface UserTokenBreakdown {
  user_id: number
  name: string
  username: string | null
  period_tokens: number
  input_tokens: number
  output_tokens: number
  cost_usd: number
  quota_used: number
  quota_limit: number
  quota_pct: number
  avg_rating: number | null
}

export interface StatsDateRange {
  from: string  // YYYY-MM-DD
  to: string    // YYYY-MM-DD
}

export interface RecentQuestion {
  id: number
  conversation_id: number | null
  user_name: string
  username: string | null
  question: string
  question_full: string | null
  answer: string | null
  asked_at: string
  rating: number | null
  token_used: number | null
}

export interface LowRatedQuestion {
  user_name: string
  username: string | null
  conversation_id: number | null
  question: string | null
  question_full: string | null
  answer: string | null
  rating: number
  comment: string | null
  asked_at: string
}

export interface RecentComment {
  user_name: string
  username: string | null
  comment: string
  rating: number | null
  question: string | null
  commented_at: string
}

export interface StatsResponse {
  period: StatsPeriod
  kpis: {
    questions: KpiStat
    tokens: KpiStat
    rating: KpiStat
  }
  question_timeseries: TimeSeries
  token_timeseries: TimeSeries
  user_token_breakdown: UserTokenBreakdown[]
  rating_distribution: TimeSeries
  recent_activity: {
    recent_questions: RecentQuestion[]
    low_rated: LowRatedQuestion[]
    recent_comments: RecentComment[]
  }
}

export interface QuestionDetail {
  question: string
  answer: string | null
  user_name: string | null
  username: string | null
  asked_at: string | null
  rating: number | null
}

export interface QuestionSource {
  file_name: string
  file_id?: string
  folder?: string
  citation?: string
  yasa_adi?: string
  chunk_count?: number
  [key: string]: unknown
}

export interface UserQuestionItem {
  id: number
  question: string
  question_full: string | null
  answer: string | null
  sources: QuestionSource[] | null
  asked_at: string
  rating: number | null
  comment: string | null
  token_used: number | null
}

export interface UserQuestionsResponse {
  user: { name: string; username: string | null; email: string }
  total: number
  page: number
  per_page: number
  last_page: number
  rated_count: number
  unrated_count: number
  avg_rating: number | null
  questions: UserQuestionItem[]
}

export function fetchUserQuestions(
  username: string,
  period: StatsPeriod = 'month',
  dateRange?: StatsDateRange,
  page = 1,
  perPage = 20
): Promise<{ data: UserQuestionsResponse }> {
  const params: Record<string, string | number> = { page, per_page: perPage }
  if (period === 'custom' && dateRange) {
    params.from = dateRange.from
    params.to   = dateRange.to
  } else {
    params.period = period
  }
  return axios.get(`${API_BASE_URL}/admin/users/${username}/questions`, { params })
}

export function fetchQuestionDetail(id: number): Promise<{ data: QuestionDetail }> {
  return axios.get(`${API_BASE_URL}/admin/stats/questions/${id}`)
}

export function fetchAdminStats(
  period: StatsPeriod,
  dateRange?: StatsDateRange
): Promise<{ data: StatsResponse }> {
  if (period === 'custom' && dateRange) {
    return axios.get(`${API_BASE_URL}/admin/stats`, {
      params: { from: dateRange.from, to: dateRange.to },
    })
  }
  return axios.get(`${API_BASE_URL}/admin/stats`, { params: { period } })
}
